"use client";

import React from "react";

export type GigPostingType = {
    id: string;
    gig_type_id: string;
    gig_type_name: string;
    owning_entity_type: string;
    owning_entity_id: number;
    owning_entity_name?: string;
    opens_at: string | null;
    closes_at: string | null;
    count_needed: number;
    filled_count: number;
    status: string;
    geographic_scope: string | null;
    program_journey_scope?: string | null;
    membership_journey_scope?: string | null;
    description: string | null;
    time_commitment: string | null;
    cadence: string;
    recognition_tier: string;
    is_confidential_intake: boolean;
    application_status?: string | null;
};

interface GigCardProps {
    gig: GigPostingType;
    onClick: () => void;
    hasAccess?: boolean;
}

// Map entity type to specific accent stripe color matching reference
const ACCENT_COLORS: Record<string, string> = {
    committee: "bg-[#004d57]", // deep teal
    chapter: "bg-[#4b8892]",    // mid teal
    community: "bg-[#a9c4bb]",  // sage
    region: "bg-[#c3d6d9]"      // mist
};

export default function GigCard({ gig, onClick, hasAccess = true }: GigCardProps) {
    const accentColor = ACCENT_COLORS[gig.owning_entity_type.toLowerCase()] || "bg-[#4b8892]";

    // Format owning group/entity label
    const owningGroupLabel = gig.owning_entity_name || 
        (gig.owning_entity_type.charAt(0).toUpperCase() + gig.owning_entity_type.slice(1));

    // Check if it has local/support chapters
    const hasSupport = gig.gig_type_name.toLowerCase().includes("campus") || 
        gig.owning_entity_type.toLowerCase() === "chapter";
    const supportLabel = "Local chapters";

    // Format cadence text
    const cadenceText = (gig.cadence && gig.cadence !== "null") ? gig.cadence.replace(/_/g, " ") : "ongoing";

    const cadenceLabel = cadenceText.charAt(0).toUpperCase() + cadenceText.slice(1);

    // Get eligibility text and style matching reference
    const getEligibilityInfo = () => {
        const progScope = (gig.program_journey_scope || "").toLowerCase();
        const memScope = (gig.membership_journey_scope || "").toLowerCase();
        const hasGeoScope = gig.geographic_scope && gig.geographic_scope !== "null";
        
        if (progScope.includes("charter")) {
            return { label: "CMT charterholders", bg: "bg-[#a9c4bb]/30", text: "text-[#004d57]" };
        }
        if (memScope.includes("member") || memScope.includes("candidate")) {
            return { label: "Members & candidates", bg: "bg-[#c3d6d9]/35", text: "text-[#004d57]" };
        }
        if (hasGeoScope) {
            return { label: "Members in the region", bg: "bg-[#c3d6d9]/35", text: "text-[#004d57]" };
        }
        return { label: "Open to all", bg: "bg-slate-100", text: "text-[#004d57]" };
    };

    const elig = getEligibilityInfo();
    const stillNeeded = Math.max(0, (Number(gig.count_needed) || 0) - (Number(gig.filled_count) || 0));

    return (
        <div 
            onClick={hasAccess ? onClick : undefined}
            className={`group bg-white border border-gray-150 rounded-xl shadow-sm flex flex-col justify-between font-sans overflow-hidden ${
                hasAccess 
                    ? "hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer" 
                    : "opacity-75 cursor-not-allowed"
            }`}
        >
            {/* Top 4px Accent Stripe */}
            <div className={`h-[4px] w-full ${accentColor}`} />

            <div className="p-[18px] flex flex-col gap-3 flex-1">
                {/* Owning Group & Support Pills */}
                <div className="flex flex-wrap items-center gap-1.5">
                    {gig.owning_entity_type.toLowerCase() !== "staff" && (
                        <span className="font-sans font-bold text-[10px] text-[#004d57] bg-[#f9d2d3] px-2.5 py-1 rounded-full uppercase tracking-[0.07em]">
                            {owningGroupLabel}
                        </span>
                    )}
                    {hasSupport && (
                        <span className="font-sans font-bold text-[10px] text-[#004d57] bg-[#c3d6d9]/40 px-2.5 py-1 rounded-full uppercase tracking-[0.07em]">
                            + {supportLabel}
                        </span>
                    )}
                    {gig.application_status && (
                        <span className={`font-sans font-bold text-[9px] px-2 py-0.5 rounded-full uppercase tracking-widest border ${
                            gig.application_status === "approved"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : gig.application_status === "rejected"
                                ? "bg-rose-50 text-rose-700 border-rose-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"
                        }`}>
                            {gig.application_status === "approved" ? "Accepted" : gig.application_status === "rejected" ? "Rejected" : "Applied"}
                        </span>
                    )}
                </div>

                {/* Title & Description */}
                <div>
                    <h3 className="font-sans font-bold text-base text-[#004d57] group-hover:text-[#4b8892] transition-colors leading-snug">
                        {gig.gig_type_name}
                    </h3>
                    <p className="text-xs text-gray-500 line-clamp-2 mt-1.5 leading-relaxed">
                        {gig.description || ""}
                    </p>
                </div>

                {/* Cadence + Eligibility Badges */}
                <div className="flex flex-wrap gap-1.5 mt-1">
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 border border-gray-200 bg-white px-2.5 py-1 rounded-full whitespace-nowrap">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#4b8892] shrink-0" />
                        {cadenceLabel}
                    </span>
                    <span className={`inline-flex items-center text-[11px] font-medium ${elig.bg} ${elig.text} px-2.5 py-1 rounded-full whitespace-nowrap`}>
                        {elig.label}
                    </span>
                </div>

                <div className="flex-1" />

                {/* Bottom Stats & CTA */}
                <div className="border-t border-gray-100 pt-3 mt-3 flex items-center justify-between gap-4">
                    <div>
                    </div>
                    {hasAccess ? (
                        <span className="text-xs text-[#4b8892] font-bold group-hover:text-[#007a8a] transition-colors whitespace-nowrap">
                            Read brief →
                        </span>
                    ) : (
                        <span className="text-xs text-gray-400 font-bold whitespace-nowrap">
                            Locked
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

